// 把 route.ts 认出来的「在哪儿找」换成具体的那几篇：分区的文件夹列表、分区里的每一篇（都缓存几分钟）。
// 出任何错都当没认出来，照原来的范围问，不挡着提问。

import { useCallback, useEffect } from 'react'
import { useQueries, useQueryClient, type QueryClient } from '@tanstack/react-query'
import { getFolderTree, listKnowledge } from '../../api/kb'
import { MINE_FOLDER, parseNeed, routeFolder, type FolderRef, type Routed } from './route'

const STALE = 5 * 60_000

const foldersOf = (qc: QueryClient, kbId: string) => qc.fetchQuery({ queryKey: ['folders', kbId], queryFn: () => getFolderTree(kbId), staleTime: STALE })
const folderDocs = (qc: QueryClient, kbId: string, folder: string) =>
  qc.fetchQuery({ queryKey: ['route-folder', kbId, folder], queryFn: () => listKnowledge(kbId, { folderPath: folder, recursive: true, pageSize: 1000 }).then((r) => r.data ?? []), staleTime: STALE })

/**
 * 返回 route(query, kbIds)：kbIds 是这次本来要查的库。认出了就给缩小后的范围，否则 null。
 * warmKbIds：先把这几个库的分区取回来，提问时不用等。
 */
export function useQuestionRouter(warmKbIds?: string[]) {
  const qc = useQueryClient()
  const warm = (warmKbIds ?? []).join(',')

  useEffect(() => {
    if (!warm) return
    for (const id of warm.split(',')) void foldersOf(qc, id).catch(() => undefined)
  }, [qc, warm])

  return useCallback(
    async (query: string, kbIds: string[]): Promise<Routed | null> => {
      try {
        const trees = await Promise.all(kbIds.map(async (kbId) => ({ kbId, tree: await foldersOf(qc, kbId) })))
        const folders: FolderRef[] = trees.flatMap(({ kbId, tree }) => (tree?.folders ?? []).map((f) => ({ kbId, path: f.path.replace(/^\/+/, '') })))
        const need = parseNeed(query, folders)
        if (!need) return null
        return routeFolder(need, await folderDocs(qc, need.kbId, need.folder))
      } catch {
        return null
      }
    },
    [qc],
  )
}

/** 依据旁边标的来源：我写的、项目资料。别的不标。 */
const TAGGED = [MINE_FOLDER, '项目资料'] as const
const TAG_NAME: Record<(typeof TAGGED)[number], string> = { 我写的: '我写的', 项目资料: '项目' }

export function useSourceTag(kbIds: string[]): (s: { knowledgeId?: string }) => string | null {
  const lists = useQueries({
    queries: kbIds.flatMap((kbId) =>
      TAGGED.map((folder) => ({
        queryKey: ['route-folder', kbId, folder],
        queryFn: () => listKnowledge(kbId, { folderPath: folder, recursive: true, pageSize: 1000 }).then((r) => r.data ?? []),
        staleTime: STALE,
      })),
    ),
  })
  const ids = new Map<string, string>()
  lists.forEach((q, i) => {
    const tag = TAG_NAME[TAGGED[i % TAGGED.length]]
    for (const d of q.data ?? []) ids.set(d.id, tag)
  })
  return (s) => (s.knowledgeId && ids.get(s.knowledgeId)) || null
}
